import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorScreen } from 'src/components';
import { Loading } from 'src/components/Loading';
import { useClientDeliveryRequestTrackingFetcher } from 'src/hooks/useClientDeliveryRequestTrackingFetcher';
import { useComputeClientDeliveryRequests } from 'src/hooks/useComputeClientDeliveryRequests';

import { useParams } from 'react-router-dom';

export type Props = {
  token: string;
};

export const PublicClientDeliveryRequestPaymentSuccess: FC<Props> = () => {
  const { t } = useTranslation();

  const params = useParams<{ token: string }>();

  const { token } = params;

  const { fetchData, isLoading, error, data } = useClientDeliveryRequestTrackingFetcher();

  const trackingSteps = useComputeClientDeliveryRequests(data?.history ?? []);

  useEffect(() => {
    fetchData(token);
  }, [fetchData, token]);

  const retryReload = useCallback(() => {
    fetchData(token);
  }, [fetchData, token]);

  if (isLoading) {
    return <Loading />;
  }

  if (error || !data) {
    return <ErrorScreen error={error ?? new Error('Aucune demande trouvé')} retry={retryReload} />;
  }

  return (
    <div className="flex flex-col items-center min-h-screen bg-gray-50 py-10 px-4">
      {/* Header Section */}
      <div className="w-full max-w-xl bg-white rounded-lg shadow-md">
        <div className="bg-blue-600 text-white py-4 px-6 rounded-t-lg">
          <h1 className="text-lg font-semibold">{t('client-delivery-requests.page-title')}</h1>
        </div>
        <div className="p-6">
          <p
            className="mb-4 text-gray-700"
            dangerouslySetInnerHTML={{ __html: t('client-delivery-requests.hello', { name: data.name }) }}
          />

          {data.paymentStatus === 'PAID' && (
            <p className="text-green-700 mb-6">{t('client-delivery-requests.payment-sucess')}</p>
          )}

          {/* Delivery Information */}
          <div className="bg-gray-100 rounded-md p-4 border-l-4 border-blue-500 mb-6">
            <p className="text-sm text-gray-600">
              <strong>Envoi Numéro :</strong> {data.shipping.tracker}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Numéro de la demande :</strong>
              {data.tracker}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Colis livré à l'agence :</strong> {data.shipping?.toAgency?.name}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Adresse de livraison :</strong> {data.address}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
